import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  input,
  Output,
  output,
  signal,
} from '@angular/core';
import moment from 'moment';
import { tap } from 'rxjs';
import { ListFilterComponent } from './list-filter/list-filter.component';
import {
  FiltersRegion,
  FiltersLocation,
} from '../../interfaces/region.interface';
import { PageContainerService } from '../../services/page-container.service';
import { LocationsFilterPipe } from '../bc-top-header-filters/locationsFilter.pipe';

@Component({
  selector: 'z-mobile-header-filters',
  standalone: true,
  imports: [CommonModule, ListFilterComponent, LocationsFilterPipe],
  templateUrl: './mobile-header-filters.component.html',
  styleUrls: ['./mobile-header-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('drawerFilterState', [
      state(
        'open',
        style({
          width: '100%',
          opacity: 1,
        }),
      ),
      state(
        'closed',
        style({
          width: '0%',
          opacity: 0,
          display: 'none',
        }),
      ),
      transition('closed => open', [animate('350ms linear')]),
      transition('open => closed', [animate('350ms linear')]),
    ]),
    trigger('drawerFilterContentState', [
      state(
        'open',
        style({
          width: '100%',
          opacity: 1,
        }),
      ),
      state(
        'closed',
        style({
          width: '0%',
          opacity: 0,
        }),
      ),
      transition('closed => open', [animate('350ms linear')]),
      transition('open => closed', [animate('350ms linear')]),
    ]),
  ],
})
export class MobileHeaderFiltersComponent {
  regions = input.required<FiltersRegion[]>();
  locations = input.required<FiltersLocation[]>();
  locationsFilters = output<FiltersLocation[]>();

  @Input({ required: true }) selectedRegions: FiltersRegion[] = [];
  @Output() selectedRegionsChange = new EventEmitter<any>();

  @Input({ required: true }) selectedLocations: FiltersLocation[] = [];
  @Output() selectedLocationsChange = new EventEmitter<any>();

  showFilters = signal(false);

  dateTime = signal<{ date: string; time: string }>({ date: '', time: '' });
  intervalId!: NodeJS.Timeout;

  selectedFilterType = signal<'region' | 'location' | null>(null);

  expandMenuDrawer$ = this.pageContainerService.expandFilterDrawer$.pipe(
    tap(x => {
      if (!x) {
        this.showFilters.set(false);
      }
    }),
  );

  constructor(
    readonly pageContainerService: PageContainerService,
    readonly cd: ChangeDetectorRef,
  ) {
    this.dateTime.set(this.formatDateTime());
  }

  ngOnInit() {
    this.dateTime.set(this.formatDateTime());
    this.intervalId = setInterval(() => {
      this.dateTime.set(this.formatDateTime());
    }, 60000);
  }

  filter(event: FiltersLocation[]) {
    this.locationsFilters.emit(event);
  }

  formatDateTime(): { date: string; time: string } {
    const now = moment();
    const formattedDate = now.format('dddd, MMMM DD, YYYY'); // Sunday, March 19, 2023
    const formattedTime = now.format('hh:mm A [(GMT]Z[)]'); // 04:29 PM (GMT+2)

    return {
      date: formattedDate,
      time: formattedTime,
    };
  }

  closeMenu() {
    this.pageContainerService.expandFilterDrawer$.next(false);
  }

  showFiltersContent(type: 'region' | 'location') {
    this.selectedFilterType.set(type);
    this.showFilters.set(true);
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  onRegionChange() {
    this.selectedLocations = this.selectedLocations.filter(l =>
      this.selectedRegions.some(r => r.id === l.regionId),
    );
    this.selectedLocationsChange.emit(this.selectedLocations);
    this.locationsFilters.emit(this.selectedLocations);
  }

  onLocationChange() {
    this.locationsFilters.emit(this.selectedLocations);
  }

  regionsChange(ev: FiltersRegion[]) {
    this.selectedRegionsChange.emit(ev);
    this.cd.detectChanges();
  }

  locationsChange(ev: FiltersLocation[]) {
    this.selectedLocationsChange.emit(ev);
    this.cd.detectChanges();
  }
}
